import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * The frame of the no-login client pages reached by a secure link
 * (prototype `clientFrame()` in `docs/discovery/prototype/discovery.js`):
 * the Avero mark, who the link is for, one card, and a footer saying the
 * link works only for the person it was sent to. T-0184 uses it for the
 * questionnaire preview; the live client pages (T-0191) use it too.
 */

export function ClientFrame({
  forLine,
  wide = false,
  children,
}: {
  /** Right of the logo, for example "Secure link for City of Marlow · no account needed". */
  forLine: React.ReactNode;
  /** The map validation page's wider column (1180px). */
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-center px-4 pb-16 pt-10">
      <div className={cn('flex max-w-full flex-col gap-4', wide ? 'w-[1180px]' : 'w-[720px]')}>
        <div className="flex items-center justify-between gap-3">
          <img src="/avero-logo.png" alt="Avero Advisors" className="h-9 w-auto" />
          <span className="text-meta text-ink-500">{forLine}</span>
        </div>
        {children}
        <p className="text-center text-meta text-ink-500">
          This link was sent to you by Avero Advisors and works only for you. Questions? Reply to
          the email it came in.
        </p>
      </div>
    </div>
  );
}

/**
 * A card of a client page (`.client-card`); `center` is the centred
 * thank-you card (`.client-card.center-col`).
 */
export function ClientCard({
  center = false,
  className,
  children,
}: {
  center?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-large border border-ink-100 bg-paper-0 shadow-modal',
        center ? 'flex flex-col items-center gap-2.5 px-8 py-12 text-center' : 'px-8 py-7',
        className,
      )}
    >
      {children}
    </div>
  );
}
